import { useState, useCallback, useMemo, useEffect } from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { useToast } from "@/client/components/common/toast"
import styles from "./login-page.module.css"

type LoginMethod = "phone" | "email" | "wallet"

// 登录频率限制常量
const LOGIN_MAX_ATTEMPTS = 5
const LOGIN_WINDOW_MS = 5 * 60 * 1000 // 5分钟窗口

// 获取登录尝试记录
function getLoginAttempts(): { count: number; resetTime: number } {
  try {
    const stored = sessionStorage.getItem("login_attempts")
    if (stored) {
      const data = JSON.parse(stored)
      if (Date.now() > data.resetTime) {
        sessionStorage.removeItem("login_attempts")
        return { count: 0, resetTime: 0 }
      }
      return data
    }
  } catch {
    // ignore
  }
  return { count: 0, resetTime: 0 }
}

// 记录登录尝试
function recordLoginAttempt(): { count: number; resetTime: number } {
  const current = getLoginAttempts()
  const now = Date.now()
  const resetTime = current.resetTime || (now + LOGIN_WINDOW_MS)
  const count = current.count + 1
  const data = { count, resetTime }
  sessionStorage.setItem("login_attempts", JSON.stringify(data))
  return data
}

// 清除登录尝试记录
function clearLoginAttempts() {
  sessionStorage.removeItem("login_attempts")
}

// Mail Icon SVG
const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
)

// Phone Icon SVG
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
  </svg>
)

// Wallet Icon SVG
const WalletIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12V7H5a2 2 0 010-4h14v4"/>
    <path d="M3 5v14a2 2 0 002 2h16v-5"/>
    <path d="M18 12a2 2 0 100 4 2 2 0 000-4z"/>
  </svg>
)

// Logo Icon SVG
const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
)

// Sparkle/Gift Icon
const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z"/>
  </svg>
)

// Eye Icon (for show/hide password)
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
)

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signInWithEmail, signInWithPhone, isLoading, error, clearError } = useAuthStore()
  const toast = useToast()

  const [method, setMethod] = useState<LoginMethod>("phone")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [code, setCode] = useState("")
  const [countdown, setCountdown] = useState(0)
  const [agreed, setAgreed] = useState(false)
  const [loginAttempts, setLoginAttempts] = useState(() => getLoginAttempts())

  // 检查是否被锁定
  const isLocked = useMemo(() => {
    return loginAttempts.count >= LOGIN_MAX_ATTEMPTS
  }, [loginAttempts])

  // 计算剩余锁定时间
  const lockRemainingTime = useMemo(() => {
    if (!isLocked) return 0
    return Math.max(0, Math.ceil((loginAttempts.resetTime - Date.now()) / 1000))
  }, [isLocked, loginAttempts])

  // 倒计时更新器
  useEffect(() => {
    if (!isLocked) return
    const interval = setInterval(() => {
      setLoginAttempts(getLoginAttempts())
    }, 1000)
    return () => clearInterval(interval)
  }, [isLocked])

  const redirectUrl = searchParams.get("redirect") || "/me"

  const startCountdown = () => {
    setCountdown(60)
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleSendCode = async () => {
    if (countdown > 0 || phone.length !== 11) return
    clearError()
    try {
      const result = await signInWithPhone("+86" + phone)
      if (result.requiresOtp) {
        startCountdown()
        toast.success("验证码已发送")
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "发送验证码失败，请稍后重试")
    }
  }

  const handlePhoneLogin = async () => {
    clearError()
    try {
      await signInWithPhone("+86" + phone, code)
      clearLoginAttempts()
      toast.success("登录成功")
      navigate(redirectUrl)
    } catch (err) {
      const attempts = recordLoginAttempt()
      setLoginAttempts(attempts)
      toast.error(err instanceof Error ? err.message : "登录失败，请重试")
    }
  }

  const handleEmailLogin = async () => {
    clearError()
    try {
      await signInWithEmail(email, password)
      clearLoginAttempts()
      toast.success("登录成功")
      navigate(redirectUrl)
    } catch (err) {
      const attempts = recordLoginAttempt()
      setLoginAttempts(attempts)
      toast.error(err instanceof Error ? err.message : "登录失败，请重试")
    }
  }

  const handleLogin = () => {
    if (isLocked) {
      const minutes = Math.ceil(lockRemainingTime / 60)
      toast.warning(`登录尝试次数过多，请在 ${minutes} 分钟后重试`)
      return
    }

    if (method === "phone") {
      handlePhoneLogin()
    } else if (method === "email") {
      handleEmailLogin()
    }
  }

  const isPhoneValid = phone.length === 11 && code.length >= 4 && agreed
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && password.length >= 6 && agreed

  return (
    <div className={styles.page}>
      {/* Hero Section */}
      <div className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroPattern} />
        <div className={styles.heroContent}>
          <div className={styles.logo}>
            <LogoIcon />
          </div>
          <h1 className={styles.title}>蓝血精英</h1>
          <p className={styles.subtitle}>发现 · 连接 · 成长</p>
        </div>
      </div>

      {/* Bonus Banner */}
      <div className={styles.bonusBanner}>
        <div className={styles.bonusIcon}>
          <SparkleIcon />
        </div>
        <div className={styles.bonusText}>
          <div className={styles.bonusTitle}>新用户专享</div>
          <div className={styles.bonusDesc}>注册即送 ¥100 体验金</div>
        </div>
      </div>

      {/* Form Container */}
      <div className={styles.formContainer}>
        {/* Login Method Tabs */}
        <div className={styles.methodTabs}>
          <button
            className={`${styles.methodTab} ${method === "phone" ? styles.activeTab : ""}`}
            onClick={() => setMethod("phone")}
          >
            <PhoneIcon />
            <span>手机登录</span>
          </button>
          <button
            className={`${styles.methodTab} ${method === "email" ? styles.activeTab : ""}`}
            onClick={() => setMethod("email")}
          >
            <MailIcon />
            <span>邮箱登录</span>
          </button>
          <button
            className={`${styles.methodTab} ${method === "wallet" ? styles.activeTab : ""}`}
            onClick={() => setMethod("wallet")}
          >
            <WalletIcon />
            <span>钱包连接</span>
          </button>
        </div>

        {/* 锁定提示 */}
        {isLocked && (
          <div className={styles.lockNotice}>
            <span className={styles.lockIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
            </span>
            <div className={styles.lockText}>
              <span>登录尝试次数过多</span>
              <span className={styles.lockTimer}>
                {lockRemainingTime > 60
                  ? `${Math.floor(lockRemainingTime / 60)}分${lockRemainingTime % 60}秒后重试`
                  : `${lockRemainingTime}秒后重试`}
              </span>
            </div>
          </div>
        )}

        {/* Phone Login Form */}
        {method === "phone" && (
          <div className={styles.form}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>手机号</label>
              <div className={styles.inputWrapper}>
                <span className={styles.inputIcon}><PhoneIcon /></span>
                <input
                  type="tel"
                  className={styles.input}
                  placeholder="请输入手机号"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  maxLength={11}
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>验证码</label>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  className={`${styles.input} ${styles.inputWithBtn}`}
                  placeholder="请输入验证码"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <button
                  className={styles.codeBtn}
                  onClick={handleSendCode}
                  disabled={countdown > 0 || phone.length !== 11 || isLoading}
                >
                  {countdown > 0 ? `${countdown}s` : "获取验证码"}
                </button>
              </div>
            </div>

            <div className={styles.checkbox}>
              <input
                type="checkbox"
                id="agree"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <label htmlFor="agree">
                我已阅读并同意 <a href="#terms">《用户协议》</a> 和 <a href="#privacy">《隐私政策》</a>
              </label>
            </div>

            <button
              className={styles.loginBtn}
              onClick={handleLogin}
              disabled={!isPhoneValid || isLoading}
            >
              {isLoading ? "登录中..." : "登录 / 注册"}
            </button>

            <div className={styles.divider}>
              <span className={styles.dividerLine} />
              <span className={styles.dividerText}>其他方式</span>
              <span className={styles.dividerLine} />
            </div>

            <button className={styles.walletBtn}>
              <WalletIcon />
              <span>连接 Web3 钱包</span>
            </button>
          </div>
        )}

        {/* Email Login Form */}
        {method === "email" && (
          <div className={styles.form}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>邮箱</label>
              <div className={styles.inputWrapper}>
                <span className={styles.inputIcon}><MailIcon /></span>
                <input
                  type="email"
                  className={styles.input}
                  placeholder="请输入邮箱地址"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>密码</label>
              <div className={styles.inputWrapper}>
                <input
                  type="password"
                  className={styles.input}
                  placeholder="请输入密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {/* 错误消息 */}
            {error && (
              <div className={styles.errorMsg}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{error}</span>
              </div>
            )}

            <div className={styles.forgotLink}>
              <Link to="/forgot-password">忘记密码？</Link>
            </div>

            <div className={styles.checkbox}>
              <input
                type="checkbox"
                id="agree-email"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <label htmlFor="agree-email">
                我已阅读并同意 <a href="#terms">《用户协议》</a> 和 <a href="#privacy">《隐私政策》</a>
              </label>
            </div>

            <button
              className={styles.loginBtn}
              onClick={handleLogin}
              disabled={!isEmailValid || isLoading}
            >
              {isLoading ? "登录中..." : "登录 / 注册"}
            </button>

            <div className={styles.divider}>
              <span className={styles.dividerLine} />
              <span className={styles.dividerText}>其他方式</span>
              <span className={styles.dividerLine} />
            </div>

            <button className={styles.walletBtn}>
              <WalletIcon />
              <span>连接 Web3 钱包</span>
            </button>
          </div>
        )}

        {/* Wallet Login Form */}
        {method === "wallet" && (
          <div className={styles.form}>
            <button className={styles.walletBtn}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M21.967 11.6l-7-4.8a1 1 0 00-.934 0l-7 4.8a1 1 0 001.255 1.238L8 16.8V22l3.5-2 3.5 2v-5.2l3.667-5.8a1 1 0 00.535-1.4z"/>
              </svg>
              <span>连接 MetaMask 钱包</span>
            </button>

            <button className={styles.walletBtn}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span>连接 Coinbase 钱包</span>
            </button>

            <button className={styles.walletBtn}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18l6.9 3.45L12 11.09 5.1 7.63 12 4.18z"/>
              </svg>
              <span>连接 WalletConnect</span>
            </button>

            <div className={styles.terms}>
              登录即表示您同意我们的 <a href="#terms">服务条款</a>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        还没有账号？
        <Link to="/register" className={styles.footerLink}>立即注册</Link>
      </div>
    </div>
  )
}